import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Images, Loader2, MessageCircle } from "lucide-react";
import { buildWhatsAppMessage, whatsappLink, copyText } from "../../lib/whatsapp";
import { useClient } from "../../context/ClientContext";

const canShareFiles = () => !!navigator.canShare?.({ files: [new File([""], "x.jpg", { type: "image/jpeg" })] });

const usePhotoShare = (po, msg) => {
  const { client, fileUrl, quick } = useClient();
  const [files, setFiles] = useState(null);
  useEffect(() => {
    if (!canShareFiles()) return;
    let alive = true;
    (async () => {
      const out = [];
      if (!quick) {
        try {
          const r = await client.get(`/pos/${po.id}/pdf`, { responseType: "blob" });
          out.push(new File([r.data], `${po.po_no}.pdf`, { type: "application/pdf" }));
        } catch {
          /* share photos even if the PDF fails */
        }
      }
      const jobs = po.items.flatMap((it, i) => (it.photos || []).map((p, j) => fetch(fileUrl(p), { credentials: "include" })
        .then((res) => (res.ok ? res.blob() : null))
        .then((b) => b && new File([b], `${po.po_no}-item${i + 1}-photo${j + 1}.jpg`, { type: "image/jpeg" }))
        .catch(() => null)));
      out.push(...(await Promise.all(jobs)).filter(Boolean));
      if (alive) setFiles(out);
    })().catch(() => alive && setFiles([]));
    return () => { alive = false; };
  }, [po, quick, client, fileUrl]);
  const share = () => {
    if (!canShareFiles() || (files && !files.length)) {
      window.open(whatsappLink(msg), "_blank", "noopener");
      toast.info(files && !files.length ? "Could not load the photos. Sent text only; attach photos manually in WhatsApp."
        : quick ? "This browser can't attach photos. Attach them manually in WhatsApp." : "This browser can't attach files. Download the PDF (it includes all photos) and attach it in WhatsApp.");
      return;
    }
    if (!files) return toast.info("Getting the photos ready — tap again in a moment");
    const expected = po.items.reduce((n, it) => n + (it.photos?.length || 0), 0) + (quick ? 0 : 1);
    if (files.length < expected) toast.info(`${expected - files.length} file(s) could not be loaded and were left out`);
    navigator.share({ files, title: po.po_no, text: msg }).catch((e) => {
      if (e?.name !== "AbortError") toast.error("Could not share the photos. Use Text only and attach them manually.");
    });
  };
  return [share, canShareFiles() && !files, quick];
};

export const ShareButtons = ({ po, settings, testid = "share" }) => {
  const msg = buildWhatsAppMessage(po, settings);
  const photos = po.items.reduce((n, it) => n + (it.photos?.length || 0), 0);
  const [share, busy] = usePhotoShare(po, msg);
  const copy = async () => (await copyText(msg)) ? toast.success("Message copied") : toast.error("Could not copy");
  return (
    <div className="grid grid-cols-2 gap-2">
      {photos > 0 && (
        <button type="button" className="btn btn-wa col-span-2" onClick={share} data-testid={`${testid}-share-photos-btn`}>
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Images size={18} />} {busy ? "Preparing photos…" : `Share with ${photos} photo${photos === 1 ? "" : "s"}`}
        </button>
      )}
      <a className={photos > 0 ? "btn btn-outline" : "btn btn-wa"} href={whatsappLink(msg)} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-whatsapp-btn`}>
        <MessageCircle size={18} /> {photos > 0 ? "Text only" : "WhatsApp"}
      </a>
      <button type="button" className="btn btn-outline" onClick={copy} data-testid={`${testid}-copy-btn`}>
        <Copy size={18} /> Copy message
      </button>
    </div>
  );
};

export const ShareBox = ({ po, settings }) => {
  const { quick } = useClient();
  const msg = buildWhatsAppMessage(po, settings);
  const photos = po.items.reduce((n, it) => n + (it.photos?.length || 0), 0);
  const what = photos === 1 ? "the item photo" : `all ${photos} item photos`;
  return (
    <div className="space-y-3">
      <ShareButtons po={po} settings={settings} testid="saved" />
      {photos > 0 && (
        <p className="rounded-lg border border-brand/30 bg-brand/5 p-3 text-sm" data-testid="photos-manual-note">
          {quick
            ? `"Share with photos" opens your phone's share menu with the message and ${what} attached in item order. Pick WhatsApp and your group.`
            : `"Share with photos" opens your phone's share menu with the message, the PO PDF and ${what} attached in item order. Pick WhatsApp and your group. The PO PDF also shows every photo under its item.`}
        </p>
      )}
      <textarea readOnly className="field mono !min-h-[320px] text-sm leading-relaxed" value={msg} data-testid="whatsapp-message-box" />
    </div>
  );
};
